/**
 * @author NikolaV
 */
import { DocumentFeatureFactory } from '@openlayers/documents/DocumentFeatureFactory'
import { latLonToEN, panTo } from '@openlayers/ol-utils'
import { DocumentViewerWithNavigation } from '@pages/map/actions/DocumentViewerWithNavigation'
import { DocumentsStore } from '@shared/documents/DocumentsStore'
import { Events, EventsGlobal } from '@shared/Events'
import { TreeNode } from '@shared/ui-native/Tree'
import { WindowManager } from '@shared/ui-react/FloatingWindow'
import { reaction } from 'mobx'
import OlCluster from 'ol/source/Cluster'
import VectorSource from 'ol/source/Vector'
import * as React from 'react'
import { ILayerWrapperOptions, LayerWrapper } from '../LayerWrapper'
import { Point as OlPoint } from 'ol/geom';
import { easeOut } from 'ol/easing';


export class DocumentsLayer extends LayerWrapper<IDocument> {
    featureType: FeatureType = 'document';
    clusterSource: OlCluster = null;

    private readonly lSource: VectorSource = new VectorSource({
        features: [],
        useSpatialIndex: true,
    });

    constructor(
        opts: ILayerWrapperOptions<IDocument>,
        private documentsStore: DocumentsStore,
        private getTreeNodeById: (id: number) => TreeNode
    ) {
        super(opts)

        this.clusterSource = new OlCluster({
            distance: 20,
            source: this.layerSource,
        });

        this.layer.setSource(this.clusterSource);
        this.layer.set('id', this.featureType);

        // icon scale
        this.setStyleFunction(feature => DocumentFeatureFactory.styleFunction(feature, this.documentsStore.iconScale));
        reaction(() => this.documentsStore.iconScale, () => this.layerSource.dispatchEvent('change'));

        // when items or filter change
        reaction(() => this.documentsStore.itemsFiltered, this.redrawDebounced);
        reaction(() => this.documentsStore.items, this.redrawDebounced);
        this.redraw();
    }

    // make fresh features
    redraw = () => {
        this.allFeatures = [];
        this.layerSource.clear(true);
        this.drawAll(this.documentsStore.itemsFiltered);
        this.layerSource.dispatchEvent('change');
    }

    redrawDebounced = _.debounce(this.redraw, 100);

    openDocumentByUuid = (uuid: string) => {
        this.openDocumentById(this.documentsStore.getByUuid(uuid).id);
    }

    openDocumentById = (id: number, docGroupIds: number[] = null) => {
        const document = this.documentsStore.getById(id)
        if (!document) {
            console.warn('Cant find document with id', id)
            return
        }

        let allVisibleItems = this.documentsStore.itemsFiltered
        if (Array.isArray(docGroupIds)) {
            allVisibleItems = docGroupIds.map(id => this.documentsStore.items.find(i => i.id == id));
        } else if (!allVisibleItems.find(a => a.id === id)) {
            allVisibleItems = [document]
        }

        let instance: DocumentViewerWithNavigation = null
        WindowManager.open({
            group: 'document-viewer',
            id: String(id),
            showCloseButton: true,
            allowMultiple: true,
            resize: 'both',
            collapsedTitle: document.name,
            onKeyDown: (e) => {
                if (e.key === 'ArrowLeft') instance.goPrev()
                else if (e.key === 'ArrowRight') instance.goNext()
                else if (e.key === 'Home') instance.goFirst()
                else if (e.key === 'End') instance.goLast()
            },
            body: React.createElement(DocumentViewerWithNavigation, {
                ref: (el) => (instance = el),
                initialId: document.id,
                queue: allVisibleItems,
                onEdit: (item) => Events.trigger('map-edit-document', item),
                onOpen: (itemId) => this.openDocumentById(itemId),
                onCenter: dic => this.centerAndHighlightDocument(document),
                onOpenFolderId: (folderId, imageId) =>
                    Events.trigger(
                        EventsGlobal.MAP_OPEN_DOCUMENTS_FOLDER_WITH_THUMBNAILS,
                        { folderId, imageId }
                    ),
                folderTreeNode: this.getTreeNodeById(document.folderId)
            })
        });

        if (!document.geographicPoint || (0 == document.geographicPoint.lat && 0 == document.geographicPoint.lon)) {
            // do not pan to zero latlon
            return;
        }

        // center point to pan to
        const availWidth = window.innerWidth - $('#map-sidebar-holder').width() + 20;
        const coord = latLonToEN(document.geographicPoint.lat, document.geographicPoint.lon);
        if (coord) {
            const pixel = this.map.getPixelFromCoordinate(coord);
            pixel[0] -= (window.innerWidth - availWidth) * 0.5;
            pixel[1] -= (window.innerHeight * 0.40);
            const newCoord = this.map.getCoordinateFromPixel(pixel);
            panTo(this.map, newCoord[0], newCoord[1]);
        }
        const feature = this.getFeatureById(document.id);
        if (feature) {
            Events.trigger('map-highlight-feature', feature)
        }
    }

    centerAndHighlightDocument = (doc: IDocument) => {
        let padding = [1, 1, 1, 1];
        let availWidth = window.innerWidth;
        if (document.querySelector('#map-sidebar-holder')) {
            padding = [window.innerHeight / 2, 1, 1, 1];
        } else {
            padding = [1, 1, window.innerHeight * 0.82, 1];
        }
        const coord = latLonToEN(doc.geographicPoint.lat, doc.geographicPoint.lon);
        if (coord) {
            this.map.getView().fit(
                new OlPoint(latLonToEN(doc.geographicPoint.lat, doc.geographicPoint.lon)),
                {
                    padding,
                    duration: 250,
                    easing: easeOut,
                    maxZoom: this.map.getView().getZoom(),
                });
        }

        const feature = this.getFeatureById(doc.id);
        Events.trigger('map-highlight-feature', feature)
    }

    fetchAndDrawFolder(): JQueryPromise<any> {
        return $.Deferred().resolve()
    }
}
